import type { ReactNode } from 'react';

import { BulkActionBar } from '@/components/table/BulkActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface TableColumn {
  readonly key: string;
  readonly label: string;
  /** A share of the table's width ("2fr") or a fixed track ("120px"). */
  readonly width: string;
  readonly align?: 'left' | 'right';
}

export interface TableRowData {
  readonly id: string;
  readonly cells: readonly ReactNode[];
  readonly onOpen?: () => void;
}

/** Kept as the old name so screens importing `TableRow` do not all churn. */
export type { TableRowData as TableRow };

export interface TableSelection {
  readonly selectedIds: readonly string[];
  readonly onToggle: (id: string) => void;
  /** Selects every row currently rendered, or clears them if all are on. */
  readonly onToggleAll: () => void;
  /** The controls the bulk bar offers. Rendered only when something is selected. */
  readonly actions: ReactNode;
  /** Names the checkbox column for a screen reader: "Select food", "Select run". */
  readonly itemLabel: string;
}

export interface DataTableProps {
  readonly caption: string;
  /** Rendered as a visible card heading. Omit for a table that is its own card. */
  readonly title?: string;
  /** Right-aligned beside the title, usually the scope's size. */
  readonly count?: ReactNode;
  readonly columns: readonly TableColumn[];
  readonly rows: readonly TableRowData[];
  readonly isLoading: boolean;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  /** Rendered inside the empty state, typically "clear filters". */
  readonly emptyAction?: ReactNode;
  readonly footer?: ReactNode;
  /** Adds a leading checkbox column and the bulk bar. */
  readonly selection?: TableSelection;
}

const SKELETON_ROWS = 8;
const SKELETON_WIDTHS = ['70%', '46%', '58%', '40%', '52%', '36%'];

/**
 * Narrower than this and a six-column operations table stops being readable,
 * so it scrolls instead.
 */
const MIN_CONTENT_WIDTH = 720;
/** The narrowest a share column may be squeezed to before the table scrolls. */
const MIN_SHARE_TRACK = 132;
/** The checkbox column `selection` prepends. Never part of a screen's columns. */
const SELECT_COLUMN: TableColumn = { key: '__select__', label: '', width: '44px' };

/**
 * The table every list screen uses.
 *
 * A real `<table>`, not a grid of divs with ARIA roles bolted on: the browser
 * then supplies the row/cell semantics for free and the markup says what it
 * is. Widths declared in `fr` are resolved here, because `fr` means nothing in
 * table layout — the column definitions stay in the same vocabulary every
 * screen already writes them in.
 */
export function DataTable({
  caption,
  title,
  count,
  columns: screenColumns,
  rows,
  isLoading,
  emptyTitle,
  emptyBody,
  emptyAction,
  footer,
  selection,
}: DataTableProps) {
  const columns = selection === undefined ? screenColumns : [SELECT_COLUMN, ...screenColumns];
  const { widths, minWidth } = resolveLayout(columns);
  const selectedIds = selection?.selectedIds ?? [];
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.includes(row.id));

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      {title === undefined ? null : (
        <header className="flex items-center gap-3 px-[18px] pt-[15px] pb-3.5">
          <h3 className="text-sm leading-none font-semibold text-foreground">{title}</h3>
          {count === undefined ? null : (
            <span className="ml-auto font-mono text-xs text-muted-foreground">{count}</span>
          )}
        </header>
      )}

      {selection === undefined || selectedIds.length === 0 ? null : (
        <BulkActionBar count={selectedIds.length} actions={selection.actions} />
      )}

      <Table
        style={{ minWidth }}
        className={cn('table-fixed', title !== undefined && 'border-t border-border')}
      >
        <TableCaption>{caption}</TableCaption>
        <colgroup>
          {columns.map((column, index) => (
            <col key={column.key} style={{ width: widths[index] }} />
          ))}
        </colgroup>

        <TableHeader>
          <TableRow className="border-b-0 bg-secondary hover:bg-secondary">
            {columns.map((column, index) => (
              <TableHead
                key={column.key}
                className={cn(
                  'h-9 px-3 text-[11.5px] font-semibold tracking-[0.055em]',
                  edgePadding(index, columns.length),
                  column.align === 'right' && 'text-right',
                )}
              >
                {column.key === SELECT_COLUMN.key && selection !== undefined ? (
                  <Checkbox
                    checked={allSelected}
                    disabled={rows.length === 0}
                    onCheckedChange={selection.onToggleAll}
                    aria-label={allSelected ? 'Clear the selection' : 'Select every row shown'}
                  />
                ) : (
                  column.label
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>

        <TableBody>
          {isLoading
            ? Array.from({ length: SKELETON_ROWS }, (_, rowIndex) => (
                <TableRow key={rowIndex} aria-hidden="true" className="border-t border-b-0 border-border">
                  {columns.map((column, cellIndex) => (
                    <TableCell
                      key={column.key}
                      className={cn('py-[9px]', edgePadding(cellIndex, columns.length))}
                    >
                      <Skeleton
                        className="h-4"
                        style={{ width: SKELETON_WIDTHS[cellIndex % SKELETON_WIDTHS.length] }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  columns={columns}
                  selection={selection}
                  isSelected={selectedIds.includes(row.id)}
                />
              ))}
        </TableBody>
      </Table>

      {isLoading ? (
        <span className="sr-only" aria-live="polite">
          Loading…
        </span>
      ) : null}

      {!isLoading && rows.length === 0 ? (
        <div className="flex flex-col items-center gap-2 px-8 py-16 text-center">
          <h3 className="text-sm font-semibold text-foreground">{emptyTitle}</h3>
          <p className="max-w-[440px] text-[13px] leading-relaxed text-muted-foreground">{emptyBody}</p>
          {emptyAction === undefined ? null : <div className="mt-2">{emptyAction}</div>}
        </div>
      ) : null}

      {footer}
    </section>
  );
}

interface RowProps {
  readonly row: TableRowData;
  readonly columns: readonly TableColumn[];
  readonly selection: TableSelection | undefined;
  readonly isSelected: boolean;
}

function Row({ row, columns, selection, isSelected }: RowProps) {
  const onOpen = row.onOpen;
  const offset = selection === undefined ? 0 : 1;

  const cells = columns.map((column, index) => (
    <TableCell
      key={column.key}
      className={cn(
        'min-w-0 py-[9px] text-[13px]',
        edgePadding(index, columns.length),
        column.align === 'right' && 'text-right',
      )}
    >
      {column.key === SELECT_COLUMN.key && selection !== undefined ? (
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => {
            selection.onToggle(row.id);
          }}
          onClick={(event) => {
            // The row may itself be a control; the checkbox is not that control.
            event.stopPropagation();
          }}
          aria-label={selection.itemLabel}
        />
      ) : (
        row.cells[index - offset]
      )}
    </TableCell>
  ));

  const className = cn('border-t border-b-0 border-border', isSelected && 'bg-secondary/60');

  if (onOpen === undefined && selection === undefined) {
    return <TableRow className={className}>{cells}</TableRow>;
  }

  /**
   * What a click on the row does depends on whether the table has a selection.
   *
   * On a selectable table the row is a *selection* target — clicking anywhere
   * on it ticks the box, which is what makes picking twenty rows bearable, and
   * opening the record is the row's own pencil. On every other table the row
   * opens the record, because there is nothing else it could usefully do.
   *
   * The keyboard opens either way. A `<tr>` cannot be a `<button>` without
   * losing its row semantics, so it takes focus and a key handler instead, and
   * Enter on a focused row meaning "open this" is the behaviour a keyboard
   * user will expect from every other table in the console.
   */
  const selects = selection !== undefined;

  return (
    <TableRow
      tabIndex={0}
      onClick={() => {
        if (selects) selection.onToggle(row.id);
        else onOpen?.();
      }}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        onOpen?.();
      }}
      className={cn(className, 'cursor-pointer hover:bg-muted/50')}
    >
      {cells}
    </TableRow>
  );
}

/** The design's wider gutter on the two outer tracks, so rows are not flush. */
function edgePadding(index: number, length: number): string {
  if (index === 0) return 'pl-[18px]';
  if (index === length - 1) return 'pr-[18px]';
  return '';
}

interface TableLayout {
  readonly widths: readonly string[];
  readonly minWidth: string;
}

/**
 * `fr` shares become percentages of the table; anything else (a `px` track)
 * passes through.
 *
 * Plain percentages, not `calc((100% - fixed) * share)`, which is what this
 * emitted before and what Chrome silently ignores on a `<col>` (and on a
 * `<th>`): every column on every list screen was rendering at the same
 * width, whatever its declared share, and nobody noticed until a screen
 * asked for a wide message column and did not get one. Percentages the
 * browser does honour, and when they add up to 100% beside a fixed pixel
 * track it scales them down proportionally to fit what the track leaves,
 * which is exactly what a share of the remainder means. The minimum width
 * still adds the fixed tracks on top of the share budget rather than
 * counting a 300px action column as if it were one 132px share: a table with
 * wide fixed tracks should scroll, not crush its content columns.
 */
function resolveLayout(columns: readonly TableColumn[]): TableLayout {
  const shares = columns.map((column) =>
    column.width.endsWith('fr') ? Number.parseFloat(column.width) : null,
  );
  const total = shares.reduce<number>((sum, share) => sum + (share ?? 0), 0);
  const fixed = columns.reduce<number>((sum, column, index) => {
    if (shares[index] !== null && shares[index] !== undefined) return sum;
    const track = Number.parseFloat(column.width);
    return sum + (Number.isNaN(track) ? 0 : track);
  }, 0);
  const shareCount = shares.filter((share) => share !== null).length;
  const shareBudget = Math.max(MIN_CONTENT_WIDTH - fixed, shareCount * MIN_SHARE_TRACK);

  const widths = columns.map((column, index) => {
    const share = shares[index];
    if (share === null || share === undefined || total <= 0) return column.width;
    const percent = Math.round((share / total) * 10000) / 100;
    return `${String(percent)}%`;
  });

  return { widths, minWidth: `${String(fixed + shareBudget)}px` };
}
